import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardBody, Col, Input, Label, Row, Container, Button, Modal, ModalHeader, ModalBody, ModalFooter, UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';
import DataTable from 'react-data-table-component';
import { MoreVertical, Trash } from 'react-feather';
import { connect } from 'react-redux';
import debounce from 'lodash.debounce';
import {
  getPlanRequests as getPlanRequestsAction,
  toggleOrganizationStatus as toggleOrganizationStatusAction,
  addContactCredit as addContactCreditAction,
  updateOrganizationSeats as updateOrganizationSeatsAction,
  subscribeOrganizationPittiPlan as subscribeOrganizationPittiPlanAction,
  subscribeHyperscoutPlan as subscribeHyperscoutPlanAction,
  subscribeCustomHyperscoutPlan as subscribeCustomHyperscoutPlanAction,
  toggleSubscription as toggleSubscriptionAction,
  activateTrial as activateTrialAction,
  deleteOrganizations as deleteOrganizationsAction
} from '../ReduxToolkit/Reducers/organizationManagement';
import { getCamelCaseWord } from '../static/brands_wholesalers_data';
import AddContactCreditModal from '../Components/AddContactCreditModal';
import EditSeatsModal from '../Components/EditSeatsModal';
import SubscribePittiPlanModal from '../Components/SubscribePittiPlanModal';
import SubscribeHyperscoutPlanModal from '../Components/SubscribeHyperscoutPlanModal';
import PlanDetailsModal from '../Components/PlanDetailsModal';
import ConfirmationModal from '../Components/ConfirmationModal';
import ActivateTrialModal from '../Components/ActivateTrialModal';

function PlanRequests({
  getPlanRequests,
  planRequests,
  totalPlanRequests,
  isLoading,
  toggleOrganizationStatus,
  addContactCredit,
  updateOrganizationSeats,
  subscribeOrganizationPittiPlan,
  subscribeHyperscoutPlan,
  subscribeCustomHyperscoutPlan,
  toggleSubscription,
  activateTrial,
  deleteOrganizations
}) {
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchText, setSearchText] = useState('');
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [tableData, setTableData] = useState([]);

  // Message modal state
  const [messageModalOpen, setMessageModalOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);

  // Current plan details modal state
  const [currentPlanModalOpen, setCurrentPlanModalOpen] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState(null);

  // Organization management modals state
  const [creditModalOpen, setCreditModalOpen] = useState(false);
  const [seatsModalOpen, setSeatsModalOpen] = useState(false);
  const [planModalOpen, setPlanModalOpen] = useState(false);
  const [hyperscoutPlanModalOpen, setHyperscoutPlanModalOpen] = useState(false);
  const [trialModalOpen, setTrialModalOpen] = useState(false);
  const [confirmationModal, setConfirmationModal] = useState(false);
  const [singleDeleteOrg, setSingleDeleteOrg] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    if (page) {
      _getTableData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, searchText, itemsPerPage]);

  useEffect(() => {
    if (planRequests[page]) {
      setTableData(planRequests[page]);
    }
  }, [planRequests, page]);

  const _getTableData = async (hideLoader = false) => {
    await getPlanRequests(page, searchText, itemsPerPage, hideLoader);
  };

  const _searchRequests = (inputValue) => {
    const query = inputValue?.trim();
    setPage(1);
    setSearchText(query);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceFn = useCallback(debounce(_searchRequests, 1000), []);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    debounceFn(e.target.value);
  };

  const handleItemPerPageSelect = async (count) => {
    setPage(1);
    setItemsPerPage(count);
  };

  // Message modal handlers
  const openMessageModal = (message) => {
    setSelectedMessage(message);
    setMessageModalOpen(true);
  };

  // Current plan details modal handler
  const openCurrentPlanModal = (org) => {
    setSelectedOrganization(org);
    setCurrentPlanModalOpen(true);
  };

  // Organization management handlers
  const openCreditModal = (org) => {
    setSelectedOrganization({
      ...org,
      organizationId: org.id,
      organizationName: org.name,
      organizationType: org.type,
      totalContactCredits: org.contactCredits,
    });
    setCreditModalOpen(true);
  };

  const openSeatsModal = (org) => {
    setSelectedOrganization(org);
    setSeatsModalOpen(true);
  };

  const openPlanModal = (org) => {
    setSelectedOrganization(org);
    setPlanModalOpen(true);
  };

  const openHyperscoutPlanModal = (org) => {
    setSelectedOrganization(org);
    setHyperscoutPlanModalOpen(true);
  };

  const openTrialModal = (org) => {
    setSelectedOrganization(org);
    setTrialModalOpen(true);
  };

  const handleAddContactCredit = async (org, credits) => {
    await addContactCredit(org.id, credits);
    _getTableData(true);
  };

  const handleUpdateSeats = async (org, seats) => {
    await updateOrganizationSeats(org.id, seats);
    _getTableData(true);
  };

  const handleSubscribePittiPlan = async (org, planId, existingPlanAction) => {
    await subscribeOrganizationPittiPlan(org.id, planId, existingPlanAction);
    _getTableData(true);
  };

  const handleSubscribeHyperscoutPlan = async (org, planId, customPlanData, existingPlanAction) => {
    if (customPlanData) {
      await subscribeCustomHyperscoutPlan(org.id, customPlanData, existingPlanAction);
    } else {
      await subscribeHyperscoutPlan(org.id, planId, existingPlanAction);
    }
    _getTableData(true);
  };

  const handleActivateTrial = async (org, trialDays, existingPlanAction) => {
    const result = await activateTrial(org.id, trialDays, existingPlanAction);
    if (result?.statusCode === 200) {
      _getTableData(true);
    }
  };

  const handleDelete = async () => {
    try {
      setDeleteLoading(true);
      const orgIds = [singleDeleteOrg];
      const result = await deleteOrganizations(orgIds);
      if (result?.statusCode === 200) {
        _getTableData();
      }
    } catch (e) {
      console.error('Delete error:', e);
    } finally {
      setDeleteLoading(false);
      setConfirmationModal(false);
      setSingleDeleteOrg(null);
    }
  };

  const columns = [
    {
      name: 'Organization Name',
      selector: (row) => row.organization?.name || '--',
      sortable: true,
      center: false,
      minWidth: '180px',
    },
    {
      name: 'Organization Type',
      cell: (row) => getCamelCaseWord(row.organization?.type || '--'),
      sortable: true,
      center: false,
      minWidth: '150px',
    },
    {
      name: 'Message',
      cell: (row) => (
        row?.message ? (
          <button
            onClick={() => openMessageModal(row.message)}
            style={{
              background: 'none',
              border: 'none',
              color: '#007bff',
              textDecoration: 'underline',
              cursor: 'pointer',
              fontSize: '14px',
              padding: 0,
            }}
          >
            View Message
          </button>
        ) : (
          <span>--</span>
        )
      ),
      center: true,
      minWidth: '140px',
    },
    {
      name: 'View Current Plan',
      cell: (row) => {
        const org = row?.organization;
        const hasPlanDetails = org?.is_premium && org?.plan_details;
        const hasTrialPlan = org?.is_on_trial && org?.plan_details?.trial_plan;

        if (hasPlanDetails || hasTrialPlan) {
          return (
            <button
              onClick={() => openCurrentPlanModal(org)}
              style={{
                background: 'none',
                border: 'none',
                color: '#007bff',
                textDecoration: 'underline',
                cursor: 'pointer',
                fontSize: '14px',
                padding: 0,
              }}
            >
              View Details
            </button>
          );
        }
        return <span>--</span>;
      },
      center: true,
      minWidth: '150px',
    },
    {
      name: 'Trial Ended',
      cell: (row) => (
        <span
          style={{
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: 'bold',
            backgroundColor: !row?.organization?.is_trial_ended ? '#fff3cd' : '#e8f5e9',
            color: !row?.organization?.is_trial_ended ? '#856404' : '#2e7d32',
          }}
        >
          {!row?.organization?.is_trial_ended ? 'No' : 'Yes'}
        </span>
      ),
      center: true,
      minWidth: '120px',
    },
    {
      name: 'Trial Ended At',
      cell: (row) => {
        const formatted = new Date(row?.organization?.trial_ends_at).toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }).replace(/ /g, "-");
        return (row?.organization?.is_trial_ended && row?.organization?.trial_ends_at) ? formatted : '--'
      },
      sortable: true,
      center: false,
      minWidth: '150px',
    },
    {
      name: 'Options',
      cell: (row) => {
        const org = row?.organization;
        return (
          <UncontrolledDropdown>
            <DropdownToggle tag="button" className="btn btn-link p-0">
              <MoreVertical size={20} color="#999" />
            </DropdownToggle>
            <DropdownMenu end>
              <DropdownItem onClick={() => openCreditModal(org)}>
                Add Contact Credit
              </DropdownItem>
              {org?.is_premium && (
                <DropdownItem onClick={() => openSeatsModal(org)}>
                  Edit Seats
                </DropdownItem>
              )}
              <DropdownItem onClick={() => openPlanModal(org)}>
                Subscribe Pitti Plan
              </DropdownItem>
              <DropdownItem onClick={() => openHyperscoutPlanModal(org)}>
                Subscribe Hyperscout Plan
              </DropdownItem>
              <DropdownItem onClick={() => openTrialModal(org)}>
                Activate Trial
              </DropdownItem>
              {/* <DropdownItem
                onClick={() => {
                  setSingleDeleteOrg(org.id);
                  setConfirmationModal(true);
                }}
                className="text-danger"
              >
                <Trash size={14} className="me-1" /> Delete
              </DropdownItem> */}
            </DropdownMenu>
          </UncontrolledDropdown>
        );
      },
      center: true,
      width: '100px',
    },
  ];

  return (
    <div className="page-body">
      <Container fluid style={{ padding: "20px 0px" }}>
        <Card>
          <CardBody>
            <Row className="align-items-center justify-content-between mb-3">
              <Col xs="auto">
                <h4 className="card-title mb-0" style={{ fontWeight: '600', fontSize: '20px' }}>
                  Plan Requests
                </h4>
              </Col>
              <Col xs="auto" className="d-flex flex-row gap-3 align-items-center">
                <Label className="mb-0">Search:</Label>
                <Input
                  type="text"
                  placeholder="Search by organization name"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  style={{ minWidth: '300px' }}
                />
              </Col>
            </Row>
            <div className="table-responsive mt-3">
              <DataTable
                striped
                pagination
                data={tableData}
                columns={columns}
                className="display"
                paginationServer
                progressPending={isLoading}
                paginationTotalRows={totalPlanRequests}
                paginationPerPage={itemsPerPage}
                paginationDefaultPage={page}
                customStyles={{ tableWrapper: { style: { minHeight: 400 } } }}
                onChangePage={(newPage) => setPage(newPage)}
                onChangeRowsPerPage={handleItemPerPageSelect}
                noDataComponent={
                  <div className="p-4 text-center text-muted">
                    No plan requests found
                  </div>
                }
              />
            </div>
          </CardBody>
        </Card>
      </Container>

      {/* Message Modal */}
      <Modal isOpen={messageModalOpen} toggle={() => setMessageModalOpen(false)} centered>
        <ModalHeader toggle={() => setMessageModalOpen(false)}>
          <span style={{ fontSize: "18px", fontWeight: "700" }}>
            Message
          </span>
        </ModalHeader>
        <ModalBody>
          <p style={{ fontSize: '15px', marginBottom: 0, whiteSpace: 'pre-wrap' }}>
            {selectedMessage || 'No message'}
          </p>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setMessageModalOpen(false)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      {/* Organization Management Modals */}
      <AddContactCreditModal
        open={creditModalOpen}
        onClose={() => {
          setCreditModalOpen(false);
          setSelectedOrganization(null);
        }}
        user={selectedOrganization}
        onSubmit={handleAddContactCredit}
      />
      <EditSeatsModal
        open={seatsModalOpen}
        onClose={() => {
          setSeatsModalOpen(false);
          setSelectedOrganization(null);
        }}
        organization={selectedOrganization}
        onSubmit={handleUpdateSeats}
      />
      <SubscribePittiPlanModal
        open={planModalOpen}
        onClose={() => {
          setPlanModalOpen(false);
          setSelectedOrganization(null);
        }}
        organization={selectedOrganization}
        onSubmit={handleSubscribePittiPlan}
        onToggleSubscription={async (org, isPaused) => {
          await toggleSubscription(org.id, isPaused);
          _getTableData(true);
        }}
      />
      <SubscribeHyperscoutPlanModal
        open={hyperscoutPlanModalOpen}
        onClose={() => {
          setHyperscoutPlanModalOpen(false);
          setSelectedOrganization(null);
        }}
        organization={selectedOrganization}
        onSubmit={handleSubscribeHyperscoutPlan}
        onToggleSubscription={async (org, isPaused) => {
          await toggleSubscription(org.id, isPaused);
          _getTableData(true);
        }}
      />
      <PlanDetailsModal
        open={currentPlanModalOpen}
        onClose={() => {
          setCurrentPlanModalOpen(false);
          setSelectedOrganization(null);
        }}
        organization={selectedOrganization}
      />
      <ConfirmationModal
        open={confirmationModal}
        onClose={() => {
          setConfirmationModal(false);
          setSingleDeleteOrg(null);
        }}
        onSubmit={handleDelete}
        isLoading={deleteLoading}
        title="Delete Organization"
        message="Are you sure you want to permanently delete this organization and ALL its users from the database? This action cannot be undone and will remove all associated data."
      />
      <ActivateTrialModal
        open={trialModalOpen}
        onClose={() => {
          setTrialModalOpen(false);
          setSelectedOrganization(null);
        }}
        organization={selectedOrganization}
        onSubmit={handleActivateTrial}
      />
    </div>
  );
}

const mapStateToProps = (state) => ({
  planRequests: state.organizationManagement.planRequests || {},
  totalPlanRequests: state.organizationManagement.totalPlanRequests || 0,
  isLoading: state.organizationManagement.isLoading,
});

const mapDispatchToProps = {
  getPlanRequests: getPlanRequestsAction,
  toggleOrganizationStatus: toggleOrganizationStatusAction,
  addContactCredit: addContactCreditAction,
  updateOrganizationSeats: updateOrganizationSeatsAction,
  subscribeOrganizationPittiPlan: subscribeOrganizationPittiPlanAction,
  subscribeHyperscoutPlan: subscribeHyperscoutPlanAction,
  subscribeCustomHyperscoutPlan: subscribeCustomHyperscoutPlanAction,
  toggleSubscription: toggleSubscriptionAction,
  activateTrial: activateTrialAction,
  deleteOrganizations: deleteOrganizationsAction,
};

export default connect(mapStateToProps, mapDispatchToProps)(PlanRequests);
